import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, first, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { ChangeCoverageFeatureFacade } from '@amfam/policy/change-coverage/feature';
import { PolicySelectors } from '@amfam/policy/data-access';
import { ICoverage, Vehicle } from '@amfam/policy/models';
import { FEATURES, VehicleCoveragesFacade } from '@amfam/policy/vehicle-coverages/data-access';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { CoverageFilter } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

@Component({
  selector: 'amfam-update-coverage',
  templateUrl: './update-coverage.component.html',
  styleUrls: ['./update-coverage.component.scss'],
  standalone: false
})
export class UpdateCoverageComponent implements OnInit, OnDestroy, AfterViewInit {
  hasError$: Observable<boolean> = this.store.select(vehicleQuery.selectHasError);

  errorList: Array<ErrorObj> = [];

  errorMessage: string;
  isPartner: boolean;
  isRecalculate = false;
  isRecalculateUpdate = false;
  initialCoverageFacadeIdLength: number;
  // Form elements
  updateCoverageForm: UntypedFormGroup = this.formBuilder.group({});

  public isPartner$ = this.store.select(BrandSelectors.selectIsPartner);

  public enableResetCoverage = false;
  public stateCode$ = this.store.select(userQuery.selectUserStateCode);
  afiCoverages$: Observable<ICoverage[]>;
  coverages$: Observable<ICoverage[]>;

  public vehicleId: string;

  // Shared subject for completing observables
  private stop$ = new Subject<void>();
  private changeCoverageText = this.copyService.getCopy('policy.changeCoverage.pageTitle');
  private changeCoveragePageHeader = this.copyService.getCopy('policy.changeCoverage.pageHeader');

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    public roleService: ImpersonateRolesService,
    private spinnerService: LoadingSpinnerService,
    private route: ActivatedRoute,
    public vehicleCoveragesFacade: VehicleCoveragesFacade,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private copyService: CopyService,
    private coveageChangeFeatureFacade: ChangeCoverageFeatureFacade
  ) {}

  loadCoverages() {
    this.store
      .select(PolicySelectors.selectAdvanceAutoPolicies)
      .pipe(
        filter(policies => policies && policies.length > 0),
        first()
      )
      .subscribe(policies => {
        const vehicleId = this.route.snapshot.params['id'];
        let vehicle: Vehicle;
        for (const policy of policies) {
          vehicle = policy.vehicles.find(v => v.vehicleId === vehicleId);
        }
        this.vehicleCoveragesFacade.changeCoverage(vehicle);
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateCoverage(payload: any) {
    this.isRecalculate = false;
    this.vehicleCoveragesFacade.updateCoverage(payload);
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  cancel() {
    if (!this.isPartner) {
      this.coveageChangeFeatureFacade.sendAnalytics({
        buttonAnalyticName: 'Cancel',
        event: FEATURES.CHANGE
      });
    }
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/overview']
      })
    );
  }

  getAcronymForCoverage(coverageName: string) {
    let acronym = '';
    switch (coverageName) {
      case 'Original Equipment Manufacturer (OEM) Parts Coverage':
        acronym = 'OEM';
        break;
      default:
        acronym = coverageName.split(' ').reduce((res, word) => (res += word[0]), '');
        break;
    }
    return acronym;
  }

  recalculate(feature: FEATURES) {
    this.isRecalculate = true;
    this.isRecalculateUpdate = true;
    //Checkbox, if unchecked then send analytics
    this.vehicleCoveragesFacade.coverages$.pipe(first()).subscribe(coverages => {
      coverages.forEach(c => {
        if (
          (!c.hasTerms ||
            (c.hasTerms && c.terms && c.terms.length > 0 && c.terms[0].options.length === 1)) &&
          !c.selected
        ) {
          const acronym = this.getAcronymForCoverage(c.name);
          const allowedCoverageAcronym = this.copyService.getCopy(
            'policy.changeCoverage.coverageCodeSendAnalytics'
          );
          if (allowedCoverageAcronym.includes(acronym) && this.isPartner) {
            this.coveageChangeFeatureFacade.sendAnalytics({
              event: FEATURES.CHANGE,
              buttonAnalyticName: `CancelCoverage${acronym}`
            });
          }
        }
      });
    });

    if (this.isPartner) {
      this.coveageChangeFeatureFacade.sendAnalytics({
        event: FEATURES.CHANGE,
        buttonAnalyticName: 'Start:Recalculate'
      });
    } else {
      setTimeout(() => {
        this.coveageChangeFeatureFacade.sendAnalytics({
          buttonAnalyticName: 'Recalculate',
          event: FEATURES.CHANGE
        });
      });
    }
    this.vehicleCoveragesFacade.saveAndRate(feature, this.vehicleId);
  }
  ngAfterViewInit() {
    this.coveageChangeFeatureFacade.sendAnalytics({
      pageAnalyticName: this.isPartner ? 'Start' : '',
      event: FEATURES.CHANGE
    });
  }

  nextStep() {
    if (!this.isPartner) {
      this.coveageChangeFeatureFacade.sendAnalytics({
        buttonAnalyticName: 'Continue',
        event: FEATURES.CHANGE
      });
    }

    this.store.dispatch(
      fromRouterActions.Go({ path: [`policies/change-coverage/${this.vehicleId}/summary`] })
    );
  }

  ngOnDestroy() {
    this.vehicleCoveragesFacade.cancelChangeCoverage();
    this.stop$.next();
    this.stop$.complete();
    this.spinnerService.stop();
  }

  onResetQuote() {
    if (!this.isPartner) {
      this.coveageChangeFeatureFacade.sendAnalytics({
        buttonAnalyticName: 'ResetCoverage',
        event: FEATURES.CHANGE
      });
    }
    this.isRecalculate = false;
    this.vehicleCoveragesFacade.resetToBaseEntity(FEATURES.CHANGE);
  }

  ngOnInit() {
    this.store
      .select(BrandSelectors.selectIsPartner)
      .pipe(take(1))
      .subscribe(isPartner => {
        // Check if user is non-AFI partner
        this.isPartner = isPartner;
      });

    this.afiCoverages$ = this.vehicleCoveragesFacade.aficoverages$.pipe(
      withLatestFrom(this.isPartner$, this.store.select(userQuery.selectUserStateCode)),
      map(([cs, isPartner, state]) => this.mapCoverages(cs, isPartner, state))
    );

    this.coverages$ = this.vehicleCoveragesFacade.coverages$.pipe(
      withLatestFrom(this.isPartner$, this.store.select(userQuery.selectUserStateCode)),
      map(([cs, isPartner, state]) => this.mapCoverages(cs, isPartner, state))
    );

    this.vehicleCoveragesFacade.ids$.pipe(takeUntil(this.stop$)).subscribe(ids => {
      // to reset the update coverage state with goback and cancel actions
      if (!this.isRecalculateUpdate) this.initialCoverageFacadeIdLength = ids.length;
    });
    this.vehicleId = this.route.snapshot.paramMap.get('id');
    this.pageTitle.set(this.changeCoverageText);
    this.dockingBarService.registerHeading(this.changeCoveragePageHeader);
  }

  private getCoveragesForState(isPartner: boolean, state: string): Map<string, CoverageFilter> {
    const coverageFilter: CoverageFilter = this.copyService.getCopy(
      isPartner
        ? 'policy.changeCoverage.partnerCoverageFilter'
        : 'policy.changeCoverage.coverageFilter'
    );
    const filterMap: Map<string, CoverageFilter> = new Map();

    Object.entries(coverageFilter).forEach(([key, filterCoverage]) => {
      if (filterCoverage && (!filterCoverage.states || filterCoverage.states?.includes(state))) {
        filterMap.set(key, filterCoverage);
      }
    });
    return filterMap;
  }

  private mapCoverages(coverages: ICoverage[], isPartner: boolean, state: string) {
    return coverages.map(c => {
      const coveragesConfig = this.getCoveragesForState(isPartner, state);
      const coverageConfig = coveragesConfig.get(c.publicID);

      if (coverageConfig?.forceCheckbox) c.hasTerms = false;

      return c;
    });
  }
}
